import {
  buildColumnSources,
  ColumnSource,
  findColumnSource,
  SheetSource,
  sortBySourceOrder,
} from "../../../sheetSources";
import type { SheetRowsRead } from "./rowReader";
import { isBlank, normalise } from "./valueParsing";
import {
  CellValue,
  JoinSkippedRow,
  MainRowsWithoutMatchCount,
  SkippedRow,
  SkippedRowReason,
  WorksheetRows,
} from "./validationTypes";

// A later sheet's row that the join skipped, with its cells under the worksheet's column names
export type JoinSkippedRowWithCells = {
  skippedRow: JoinSkippedRow;
  cells: Record<string, CellValue>;
};

// Main-sheet rows a later sheet has no match for, so that sheet's columns are blank on them
export type MainRowsWithoutMatch = {
  sheetName: string;
  rowNumbers: number[];
};

// A sheet with rows whose key column isn't chosen or isn't on the sheet
export type SheetWithoutKeyColumn = {
  sheetName: string;
  keyColumnName: string | null;
};

export type WorksheetJoin = {
  worksheet: string;
  mainSheetName: string;
  skippedRows: JoinSkippedRowWithCells[];
  // Later sheets with rows where not one row matched the main sheet, usually a wrong key column
  unmatchedSheetNames: string[];
  sheetsWithoutKeyColumn: SheetWithoutKeyColumn[];
  // A sheet in unmatchedSheetNames or sheetsWithoutKeyColumn isn't listed here, as it is already an error
  mainRowsWithoutMatch: MainRowsWithoutMatch[];
  // The joined sheet's columns and the sheet each came from
  columnSources: ColumnSource[];
};

type SourceRows = SheetSource & { rows: WorksheetRows };

// Joins a worksheet's sheets into one, matching later sheets' rows to the main sheet's on the key columns.
// Every main-sheet row is kept; later-sheet rows with no match, no key or a repeated key are skipped.
export function joinWorksheetSources(sources: SourceRows[]): { sheet: WorksheetRows; join: WorksheetJoin | null } {
  const [mainSource, ...laterSources] = sortBySourceOrder(sources);
  if (laterSources.length === 0) return { sheet: mainSource.rows, join: null };

  const columnSources = buildColumnSources(
    [mainSource, ...laterSources].map((source) => ({ ...source, columns: source.rows.sourceColumns }))
  );
  const columnNamesBySheetName = groupColumnNamesBySheetName(columnSources);
  const mainKeys = readNormalisedKeys(mainSource);
  const mainKeySet = new Set(mainKeys);
  const joinedRows = mainSource.rows.rows.map((row) => [...row]);
  const skippedRows: JoinSkippedRowWithCells[] = [];
  const unmatchedSheetNames: string[] = [];
  const sheetsWithoutKeyColumn: SheetWithoutKeyColumn[] = [];
  const mainRowsWithoutMatch: MainRowsWithoutMatch[] = [];
  const isMainMissingKeyColumn = isMissingKeyColumn(mainSource);
  if (isMainMissingKeyColumn) sheetsWithoutKeyColumn.push(describeSheetWithoutKeyColumn(mainSource));

  for (const source of laterSources) {
    const columnNames = columnNamesBySheetName.get(source.sheetName) ?? [];
    const blankRow = columnNames.map(() => "");
    if (isMainMissingKeyColumn || isMissingKeyColumn(source)) {
      if (!isMainMissingKeyColumn) sheetsWithoutKeyColumn.push(describeSheetWithoutKeyColumn(source));
      joinedRows.forEach((row) => row.push(...blankRow));
      continue;
    }

    const keyColumnName = source.keyColumnName ?? "";
    const keyValues = readKeyValues(source);
    const rowIndexByKey = new Map<string, number>();

    readNormalisedKeys(source).forEach((key, rowIndex) => {
      const base = {
        worksheet: source.worksheet,
        rowNumber: source.rows.rowNumbers[rowIndex],
        values: {},
        sheetName: source.sheetName,
      };
      const cells = Object.fromEntries(columnNames.map((name, index) => [name, source.rows.rows[rowIndex][index]]));
      const firstRowIndexWithKey = key === null ? undefined : rowIndexByKey.get(key);

      if (key === null) {
        skippedRows.push({ skippedRow: { ...base, reason: SkippedRowReason.BlankJoinKey, keyColumnName }, cells });
      } else if (firstRowIndexWithKey !== undefined) {
        skippedRows.push({
          skippedRow: {
            ...base,
            reason: SkippedRowReason.RepeatedJoinKey,
            keyColumnName,
            keyValue: keyValues[rowIndex],
            duplicateOfRowNumber: source.rows.rowNumbers[firstRowIndexWithKey],
          },
          cells,
        });
      } else {
        rowIndexByKey.set(key, rowIndex);
        if (!mainKeySet.has(key)) {
          skippedRows.push({
            skippedRow: { ...base, reason: SkippedRowReason.NoMatchingRow, mainSheetName: mainSource.sheetName },
            cells,
          });
        }
      }
    });

    const rowNumbersWithoutMatch: number[] = [];
    mainKeys.forEach((key, rowIndex) => {
      const matchedRowIndex = key === null ? undefined : rowIndexByKey.get(key);
      if (matchedRowIndex === undefined) rowNumbersWithoutMatch.push(mainSource.rows.rowNumbers[rowIndex]);
      joinedRows[rowIndex].push(...(matchedRowIndex === undefined ? blankRow : source.rows.rows[matchedRowIndex]));
    });
    const matchedRowCount = mainKeys.length - rowNumbersWithoutMatch.length;
    if (source.rows.rows.length > 0 && matchedRowCount === 0) unmatchedSheetNames.push(source.sheetName);
    else if (rowNumbersWithoutMatch.length > 0) {
      mainRowsWithoutMatch.push({ sheetName: source.sheetName, rowNumbers: rowNumbersWithoutMatch });
    }
  }

  return {
    sheet: {
      worksheet: mainSource.worksheet,
      sourceColumns: columnSources.map((column) => column.columnName),
      rows: joinedRows,
      rowNumbers: mainSource.rows.rowNumbers,
    },
    join: {
      worksheet: mainSource.worksheet,
      mainSheetName: mainSource.sheetName,
      skippedRows,
      unmatchedSheetNames,
      sheetsWithoutKeyColumn,
      mainRowsWithoutMatch,
      columnSources,
    },
  };
}

// Joins the sheets of every worksheet; a worksheet with one sheet is passed through as read.
// A sheet missing from what was read joins as an empty sheet, so it keeps its place in the order.
export function joinSheetSources(
  sheetSources: SheetSource[],
  sheetsRead: SheetRowsRead[]
): { sheets: WorksheetRows[]; joins: WorksheetJoin[] } {
  const sourcesByWorksheet = new Map<string, SourceRows[]>();
  for (const sheetSource of sheetSources) {
    const source = { ...sheetSource, rows: findSheetRows(sheetSource, sheetsRead) };
    sourcesByWorksheet.set(source.worksheet, [...(sourcesByWorksheet.get(source.worksheet) ?? []), source]);
  }

  const sheets: WorksheetRows[] = [];
  const joins: WorksheetJoin[] = [];
  for (const worksheetSources of sourcesByWorksheet.values()) {
    const { sheet, join } = joinWorksheetSources(worksheetSources);
    sheets.push(sheet);
    if (join) joins.push(join);
  }
  return { sheets, joins };
}

// The join's skipped rows, showing the values of the template fields mapped to their sheet
export function mapJoinSkippedRows(join: WorksheetJoin, worksheetMapping: Record<string, string | null>): SkippedRow[] {
  const columnNameByField = Object.entries(worksheetMapping).flatMap(([field, columnKey]) =>
    columnKey === null ? [] : [[field, findColumnSource(join.columnSources, columnKey)?.columnName] as const]
  );
  return join.skippedRows.map(({ skippedRow, cells }) => {
    const cellsByColumn = new Map(Object.entries(cells).map(([column, value]) => [normalise(column), value]));
    const values = Object.fromEntries(
      columnNameByField.map(([field, columnName]) => [
        field,
        (columnName && cellsByColumn.get(normalise(columnName))) ?? "",
      ])
    );
    return { ...skippedRow, values };
  });
}

// The main-sheet rows without a match that are still imported, leaving out rows dropped later
export function listImportedMainRowsWithoutMatch(
  join: WorksheetJoin,
  importedRowNumbers: ReadonlySet<number>
): MainRowsWithoutMatch[] {
  return join.mainRowsWithoutMatch
    .map(({ sheetName, rowNumbers }) => ({
      sheetName,
      rowNumbers: rowNumbers.filter((rowNumber) => importedRowNumbers.has(rowNumber)),
    }))
    .filter((entry) => entry.rowNumbers.length > 0);
}

export function countMainRowsWithoutMatch(
  join: WorksheetJoin,
  importedRowNumbers: ReadonlySet<number>
): MainRowsWithoutMatchCount[] {
  return listImportedMainRowsWithoutMatch(join, importedRowNumbers).map(({ sheetName, rowNumbers }) => ({
    mainSheetName: join.mainSheetName,
    sheetName,
    rowCount: rowNumbers.length,
  }));
}

function findSheetRows(sheetSource: SheetSource, sheetsRead: SheetRowsRead[]): WorksheetRows {
  const sheetRead = sheetsRead.find(
    (sheet) => sheet.sheetName === sheetSource.sheetName && sheet.worksheet === sheetSource.worksheet
  );
  return sheetRead ?? { worksheet: sheetSource.worksheet, sourceColumns: [], rows: [], rowNumbers: [] };
}

function groupColumnNamesBySheetName(columnSources: ColumnSource[]): Map<string, string[]> {
  const columnNamesBySheetName = new Map<string, string[]>();
  for (const { sheetName, columnName } of columnSources) {
    columnNamesBySheetName.set(sheetName, [...(columnNamesBySheetName.get(sheetName) ?? []), columnName]);
  }
  return columnNamesBySheetName;
}

function isMissingKeyColumn(source: SourceRows): boolean {
  return source.rows.rows.length > 0 && findKeyColumnIndex(source) < 0;
}

function describeSheetWithoutKeyColumn(source: SourceRows): SheetWithoutKeyColumn {
  return { sheetName: source.sheetName, keyColumnName: source.keyColumnName?.trim() || null };
}

// -1 when no key column is chosen or it isn't on the sheet
function findKeyColumnIndex(source: SourceRows): number {
  const keyColumn = normalise(source.keyColumnName ?? "");
  return keyColumn ? source.rows.sourceColumns.findIndex((column) => normalise(column) === keyColumn) : -1;
}

// Blank when the key column isn't on the sheet
function readKeyValues(source: SourceRows): string[] {
  const keyColumnIndex = findKeyColumnIndex(source);
  return source.rows.rows.map((row) => (keyColumnIndex < 0 ? "" : row[keyColumnIndex] ?? ""));
}

// Each row's key ready for matching; null when it is blank
function readNormalisedKeys(source: SourceRows): Array<string | null> {
  return readKeyValues(source).map((value) => (isBlank(value) ? null : normalise(value)));
}
